import React from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import DOMPurify from "dompurify";
import { api, SITE_URL } from "@/lib/api";
import { orgGraph, orgRef, SITE_ID } from "@/lib/schema";
import { useCompany } from "@/context/CompanyContext";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import FaqAccordion from "@/components/FaqAccordion";
import CtaBlock from "@/components/CtaBlock";
import NotFound from "@/pages/NotFound";
import { optimizeImg } from "@/lib/img";
import { Clock, Calendar, ArrowRight } from "lucide-react";

export default function BlogPost() {
  const { slug } = useParams();
  const { company } = useCompany();
  const { data: post, isLoading, isError } = useQuery({ queryKey: ["post", slug], queryFn: async () => (await api.get(`/blog/${slug}`)).data, retry: false });
  const { data: services = [] } = useQuery({ queryKey: ["bp-services"], queryFn: async () => (await api.get("/services")).data });
  const { data: allPosts = [] } = useQuery({ queryKey: ["bp-posts"], queryFn: async () => (await api.get("/blog")).data });

  if (isLoading) return <div className="max-w-3xl mx-auto px-4 py-24 text-slate-400">Loading…</div>;
  if (isError || !post) return <NotFound />;

  const related = (post.related_services || []).map((s) => services.find((x) => x.slug === s)).filter(Boolean);
  const morePosts = allPosts.filter((p) => p.slug !== post.slug && p.category === post.category).slice(0, 3);
  const dateStr = (post.published_at || "").slice(0, 10);

  const jsonLd = [
    ...orgGraph(company),
    {
      "@context": "https://schema.org", "@type": "BlogPosting",
      headline: post.title, description: post.excerpt, image: post.featured_image,
      author: orgRef(), publisher: orgRef(),
      datePublished: post.published_at, dateModified: post.updated_at,
      isPartOf: { "@id": SITE_ID },
      mainEntityOfPage: `${SITE_URL}/blog/${post.slug}/`,
    },
  ];

  return (
    <>
      <Seo title={post.seo_title} description={post.meta_description} path={`/blog/${post.slug}/`} image={post.featured_image} jsonLd={jsonLd} />
      <article>
        <section className="bg-navy text-white py-10 md:py-14">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="[&_*]:!text-slate-300"><Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Blog", to: "/blog" }, { label: post.category }, { label: post.title }]} /></div>
            <span className="mt-5 inline-block bg-launch text-white text-xs font-bold px-2.5 py-1 rounded-full">{post.category}</span>
            <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight">{post.title}</h1>
            <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-slate-300">
              <span className="flex items-center gap-1.5"><Calendar className="h-4 w-4" /> {dateStr}</span>
              <span className="flex items-center gap-1.5"><Clock className="h-4 w-4" /> {post.read_time} read</span>
              <span>By {post.author}</span>
            </div>
          </div>
        </section>

        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6">
          <img src={optimizeImg(post.featured_image, 1000)} alt={post.image_alt} className="w-full h-auto rounded-xl border border-slate-200 shadow-md" loading="eager" decoding="async" width="940" height="627" />
        </div>

        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <p className="text-lg text-slate-700 leading-relaxed border-l-4 border-launch pl-4 mb-8">{post.excerpt}</p>
          <div className="prose-launch max-w-none" data-testid="blog-content">
            {/^\s*<(p|h1|h2|h3|ul|ol|li|div|section)[\s>]/i.test(post.content || "")
              ? <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content, { ADD_ATTR: ["target", "rel"] }) }} />
              : <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.content}</ReactMarkdown>}
          </div>

          {related.length > 0 && (
            <div className="mt-10 bg-slate-50 border border-slate-200 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-navy mb-3">Related Services</h3>
              <div className="flex flex-wrap gap-2">
                {related.map((r) => <Link key={r.slug} to={`/${r.slug}`} className="inline-flex items-center gap-1 bg-white border border-slate-200 rounded-full px-3 py-1.5 text-sm font-medium text-navy hover:border-launch hover:text-launch transition-colors" data-testid={`bp-related-${r.slug}`}>{r.name} <ArrowRight className="h-3.5 w-3.5" /></Link>)}
              </div>
            </div>
          )}
        </div>

        {post.faqs?.length > 0 && <div className="bg-slate-100"><FaqAccordion faqs={post.faqs} /></div>}

        {morePosts.length > 0 && (
          <section className="py-14 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-2xl font-bold text-navy mb-6">Keep Reading</h2>
              <div className="grid sm:grid-cols-3 gap-6">
                {morePosts.map((p) => (
                  <Link key={p.slug} to={`/blog/${p.slug}`} className="group bg-slate-50 border border-slate-200 rounded-lg overflow-hidden hover:shadow-md transition-all" data-testid={`more-post-${p.slug}`}>
                    <img src={p.featured_image} alt={p.image_alt} className="w-full h-36 object-cover" loading="lazy" />
                    <div className="p-4"><h3 className="text-base font-semibold text-navy group-hover:text-launch transition-colors line-clamp-2">{p.title}</h3></div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </article>
      <CtaBlock />
    </>
  );
}
